import rals from "../../../../../../assets/rals.json";

// ====================== UTILITY ======================
export const MM = (mm: number) => mm / 1000;

// ====================== COSTANTI ======================
export const BORDO = 0.5;
export const SPESSORE_MURO = 0.3;
export const PROFONDITA_GARAGE_DEFAULT = 7;
export const ALTEZZA_PANNELLO_M = MM(500);
export const ALTEZZA_RAIL_M = MM(1);
export const RILIEVO_RAIL_M = MM(5);
export const PERNO_Y_OFFSET = MM(120);
export const BORDO_DEBORDANTE_M = MM(250);

// ====================== COLORI ======================
export const C_PANNELLO       = "#d4cdc6";
export const C_PANNELLO_RAIL  = "#bfb8b0";
export const C_MURO           = "#b0a090";
export const C_PORTINA_INSET  = "#0a0ee4";
export const C_MANIGLIA       = "#999999";
export const C_CORNICE        = "#533a20";
export const C_VETRO          = "#aaddff";
export const C_GARAGE_FONDO   = "#9a9080";
export const C_GARAGE_PAV     = "#8a8070";
export const C_BORDO_SCURO    = "#4a4a4a";
export const C_BORDO_MED      = "#999999";
export const C_BORDO_CHIARO   = "#888888";
export const C_VETRO_BORDO    = "#552200"; // inverted di C_VETRO

// ====================== COLORI MANIGLIERIA ======================
export const C_MANIGLIA_SILVER = "#c3c8cb"; // PLATINSILVER
export const C_MANIGLIA_BRONZE = "#7b5c42"; // PLATINBRONZE

// "RAL A SCELTA" e' l'unica verniciatura che richiede al cliente il codice RAL
export function isVerniciaturaRalAScelta(nomeVerniciatura: string | null | undefined): boolean {
  return /(^|[^a-z])ral([^a-z]|$)/i.test(nomeVerniciatura ?? "");
}

// Ricava il colore con cui disegnare la maniglieria dal nome della verniciatura
// scelta (PLATINSILVER / Platinbronze / RAL A SCELTA + codice RAL selezionato)
export function getColoreManiglia(
  nomeVerniciatura: string | null | undefined,
  ral: string | null | undefined
): string {
  const nome = nomeVerniciatura ?? "";
  if (/bronz/i.test(nome)) return C_MANIGLIA_BRONZE;
  if (/silver|argent/i.test(nome)) return C_MANIGLIA_SILVER;
  if (ral) return rals.find(r => r.RAL === ral)?.HEX ?? C_MANIGLIA_SILVER;
  return C_MANIGLIA_SILVER;
}

// Schiarisce (amount > 0) o scurisce (amount < 0) un colore esadecimale
export function shadeHexColor(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const channel = (i: number) => {
    const v = parseInt(h.substring(i, i + 2), 16);
    if (isNaN(v)) return '00';
    return Math.max(0, Math.min(255, Math.round(v + 255 * amount))).toString(16).padStart(2, '0');
  };
  return `#${channel(0)}${channel(2)}${channel(4)}`;
}

export function invertHexColor(hex: string): string {
  const h = hex.replace('#', '');
  const r = (255 - parseInt(h.substring(0, 2), 16)).toString(16).padStart(2, '0');
  const g = (255 - parseInt(h.substring(2, 4), 16)).toString(16).padStart(2, '0');
  const b = (255 - parseInt(h.substring(4, 6), 16)).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

// ====================== COLORE DI CONTRASTO ======================
export const C_ACCENTO = "#ff6a00";

/**
 * Colore che stacca rispetto a quello passato: l'inverso, con fallback su un
 * accento fisso quando l'inverso e' troppo vicino all'originale (grigi medi).
 */
export function coloreContrasto(hex: string | undefined): string {
  if (!hex) return C_ACCENTO;

  const h = hex.replace('#', '');
  if (h.length !== 6) return C_ACCENTO;

  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const distanza = Math.abs(255 - 2 * r) + Math.abs(255 - 2 * g) + Math.abs(255 - 2 * b);

  return distanza < 120 ? C_ACCENTO : invertHexColor(hex);
}

// Corpo dei tubolari di controtelaio: scuro, come nel render 2D.
export const C_TUBOLARE = "#2f2f2f";

// Area selezionabile di finestre/ventilazione: colore fisso, non piu' calcolato
// come negativo del colore pannello (dava esiti verdastri incoerenti col RAL).
export const C_AREA_SELEZIONABILE = "#8BC34A";

// ====================== DIMENSIONI FINESTRA ======================
export const FINESTRA_LARGHEZZA = MM(560);
export const FINESTRA_ALTEZZA   = MM(360);
export const CORNICE            = MM(18);

// ====================== DIMENSIONI GRIGLIA ======================
export const GRIGLIA_LARGHEZZA     = MM(344);
export const GRIGLIA_ALTEZZA       = MM(138);
export const GRIGLIA_400_LARGHEZZA = MM(400);
export const GRIGLIA_400_ALTEZZA   = MM(200);
export const GRIGLIA_CORNICE       = MM(20);

// ====================== DIMENSIONI PORTINA PEDONALE ======================
export const PORTINA_LARGHEZZA  = MM(800);
export const PORTINA_ALTEZZA    = MM(2100);
export const PORTINA_CORNICE_W  = MM(80);

// ====================== DIMENSIONI PORTINA LATERALE ======================
export const PORTINA_LAT_LARGHEZZA = MM(900);
export const PORTINA_LAT_ALTEZZA   = MM(2100);
export const PORTINA_LAT_CORNICE_W = MM(60);
export const PORTINA_LAT_GAP       = MM(400);

// ====================== TIPI ======================
export type OverbasStep = "finestratura" | "ventilazione" | string;
export type IntegratedDoorPosition = "sx" | "c" | "dx";

// ====================== POSIZIONE PORTA RISPETTO AL FORO ======================
// Il muro frontale (muro_con_squarcio) va da -SPESSORE_MURO/2 + 0.01 a
// +SPESSORE_MURO/2 + 0.01: questa e' la sua faccia interna.
export const Z_MURO_INTERNO = -SPESSORE_MURO / 2 + 0.01;

export type LatiOltreLuce = { sx: boolean; dx: boolean; traversa: boolean };

/**
 * Posizione della porta rispetto al foro (origine = spigolo basso-sinistro del foro).
 * - lato in luce:   la porta resta dentro il foro su quel lato
 * - lato oltre luce: la porta sborda sul muro su quel lato
 * In Z la porta e' dentro il foro (a filo faccia interna) se tutti i lati sono in
 * luce, altrimenti e' montata dietro il muro, appoggiata alla faccia interna.
 */
export function getPosizionePorta(
  larghezzaForo: number,
  larghezzaPorta: number,
  spessorePorta: number,
  oltre: LatiOltreLuce,
): { x: number; z: number } {
  const extra = larghezzaPorta - larghezzaForo;

  let x: number;
  if (oltre.sx === oltre.dx) {
    x = -extra / 2;   // simmetrica: centrata sul foro
  } else if (oltre.sx) {
    x = -extra;       // sborda solo a sinistra: bordo destro a filo foro
  } else {
    x = 0;            // sborda solo a destra: bordo sinistro a filo foro
  }

  const isOltreLuce = oltre.sx || oltre.dx || oltre.traversa;
  const z = isOltreLuce
    ? Z_MURO_INTERNO - spessorePorta / 2   // dietro il muro
    : Z_MURO_INTERNO + spessorePorta / 2;  // dentro il foro

  return { x, z };
}

// ====================== TRASFORMAZIONE BASCULANTE ======================
export function getBasculanteTransform(progress: number, doorHeight: number, isDebordante: boolean = true) {
  const angle   = (-Math.PI / 2) * progress;
  const pivotY  = doorHeight - PERNO_Y_OFFSET;
  const halfHeight = doorHeight / 2;
  const radiusY = halfHeight - PERNO_Y_OFFSET;
  // Il bordo inferiore del pannello (il punto più lontano dal cardine) finisce a
  // (halfHeight - radiusZ) * sin(angle) oltre lo squadro: usando come radiusZ
  // (halfHeight - sporgenza) quella sporgenza diventa una costante fissa,
  // indipendente dall'altezza della porta.
  const sporgenza = isDebordante ? BORDO_DEBORDANTE_M : PERNO_Y_OFFSET;
  const radiusZ = halfHeight - sporgenza;

  const cy   = pivotY - radiusY * Math.cos(angle);
  const cz   = radiusZ * Math.sin(angle);

  return { cy, cz, rotX: angle };
}
